import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useFeatureAccessContext } from '../../../context/featureAccess'
import { useSaveQueue } from '../../../hooks/useSaveQueue'
import type { AIOperation, MindMapNode } from '@bandos/shared'
import type {
  AISuggestion,
  SourceNodeInfo,
  UseAIConfig,
  UseAIReturn,
  AIStateSetters,
  AIRefs,
} from '../types/aiTypes'
import type { PositionCalculationDeps } from '../utils/aiPositioning'
import { loadSuggestionsFromDB } from '../utils/aiSuggestionLoader'
import {
  createGenerationMethod,
  IDEA_GENERATION_CONFIG,
  NEEDS_GENERATION_CONFIG,
  SYNTHESIZE_NEEDS_FROM_RESEARCH_CONFIG,
} from '../utils/aiGenerationMethods'
import {
  clearAllState,
  dismissSourceNode,
  handleToggleSuggestion,
  markSuggestionNodeDeleted,
} from '../utils/aiSuggestionActions'

export type { AISuggestion, SourceNodeInfo, UseAIConfig, UseAIReturn } from '../types/aiTypes'

/**
 * Hook for managing AI operations and suggestions.
 * Owns React state and delegates all logic to extracted utility functions.
 */
export function useAI({
  mindmapId,
  onAddNodes,
  onRemoveNodes,
  onSuccess,
  onError,
  onNodeAdded,
  onReady,
  onGenerateFeedbackForIdea,
  onRegenerateFeedbackForChildren,
  getEdges,
  getNodes,
}: UseAIConfig): UseAIReturn {
  const { isFeatureEnabled } = useFeatureAccessContext()

  const [isProcessing, setIsProcessing] = useState(false)
  const [currentOperation, setCurrentOperation] = useState<AIOperation | null>(null)
  const [pendingOperations, setPendingOperations] = useState<Set<AIOperation>>(new Set())
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([])
  const [error, setError] = useState<string | null>(null)
  const [sourceNodes, setSourceNodes] = useState<Map<string, SourceNodeInfo>>(new Map())
  const [suggestionsLoaded, setSuggestionsLoaded] = useState(false)

  const lastOperationRef = useRef<string>('')
  const lastNodeIdRef = useRef<string>('')
  const batchIdRef = useRef<number>(0)
  const prevMindmapIdRef = useRef<string | undefined>(undefined)
  const saveQueue = useSaveQueue()

  const setters: AIStateSetters = {
    setSuggestions,
    setSourceNodes,
    setCurrentOperation,
    setIsProcessing,
    setPendingOperations,
    setError,
  }
  const refs: AIRefs = { lastOperationRef, lastNodeIdRef, batchIdRef }
  const executorCallbacks = { onSuccess, onError, onReady, onRegenerateFeedbackForChildren }

  // Load suggestions on project switch
  useEffect(() => {
    if (!mindmapId) {
      prevMindmapIdRef.current = undefined
      clearAllState(setters, refs)
      setPendingOperations(new Set())
      setSuggestionsLoaded(false)
      return
    }
    if (mindmapId !== prevMindmapIdRef.current) {
      prevMindmapIdRef.current = mindmapId
      clearAllState(setters, refs)
      setPendingOperations(new Set())
      setSuggestionsLoaded(false)
      void loadSuggestionsFromDB(mindmapId, {
        setSuggestions,
        setSourceNodes,
        setSuggestionsLoaded,
      })
    }
  }, [mindmapId]) // eslint-disable-line react-hooks/exhaustive-deps

  const getPositionDeps = useCallback(
    (): PositionCalculationDeps => ({ getNodes, getEdges }),
    [getNodes, getEdges]
  )

  // Generation methods via factory pattern
  const generationDeps = {
    mindmapId,
    getPositionDeps,
    suggestions,
    currentOperation,
    sourceNodes,
    lastOperationRef,
    setters,
    refs,
    waitForPendingSaves: saveQueue.waitForPendingSaves,
    callbacks: executorCallbacks,
  }

  const genDeps = [
    mindmapId,
    suggestions,
    currentOperation,
    sourceNodes,
    saveQueue,
    getPositionDeps,
    onSuccess,
    onError,
    onReady,
    onRegenerateFeedbackForChildren,
  ]

  // useMemo (not useCallback) — we're caching the *result* of calling
  // createGenerationMethod, which itself returns a function. useCallback is
  // for inline functions; useMemo for derived values. eslint's exhaustive-deps
  // rule can analyze the inline factory below, so no disable comment needed.
  const generateIdeas = useMemo(
    () => createGenerationMethod(IDEA_GENERATION_CONFIG, generationDeps),
    genDeps // eslint-disable-line react-hooks/exhaustive-deps
  )
  const generateNeedsFromVenture = useMemo(
    () => createGenerationMethod(NEEDS_GENERATION_CONFIG, generationDeps),
    genDeps // eslint-disable-line react-hooks/exhaustive-deps
  )
  const synthesizeNeedsFromResearch = useMemo(
    () => createGenerationMethod(SYNTHESIZE_NEEDS_FROM_RESEARCH_CONFIG, generationDeps),
    genDeps // eslint-disable-line react-hooks/exhaustive-deps
  )

  // Suggestion actions
  const clearAll = useCallback(() => clearAllState(setters, refs), []) // eslint-disable-line react-hooks/exhaustive-deps

  const getSuggestionsForNode = useCallback(
    (nodeId: string): AISuggestion[] =>
      suggestions.filter((s) => s.originalEdge?.source === nodeId),
    [suggestions]
  )

  const handleDismissSourceNode = useCallback(
    (nodeId: string) =>
      dismissSourceNode(nodeId, mindmapId, { setSuggestions, setSourceNodes }, clearAll),
    [mindmapId, clearAll]
  )

  const toggleSuggestion = useCallback(
    (suggestionId: string) =>
      handleToggleSuggestion(
        suggestionId,
        suggestions,
        getPositionDeps,
        { setSuggestions, setSourceNodes },
        { onAddNodes, onRemoveNodes, onNodeAdded, onGenerateFeedbackForIdea },
        { autoChainEnabled: isFeatureEnabled('auto_chain_suggestions') }
      ),
    [
      suggestions,
      getPositionDeps,
      onAddNodes,
      onRemoveNodes,
      onNodeAdded,
      onGenerateFeedbackForIdea,
      isFeatureEnabled,
    ]
  )

  const isOperationProcessing = useCallback(
    (operation: AIOperation) => isProcessing && currentOperation === operation,
    [isProcessing, currentOperation]
  )

  const handleMarkDeleted = useCallback(
    (id: string) => markSuggestionNodeDeleted(id, setSuggestions),
    []
  )

  const addChatSuggestions = useCallback(
    (
      nodeId: string,
      newSuggestions: AISuggestion[],
      sourceNodeUpdate: {
        node: MindMapNode
        operation: AIOperation
        feedbackEntry?: string
      }
    ): void => {
      setSuggestions((prev) => [...prev, ...newSuggestions])
      setSourceNodes((prev) => {
        const next = new Map(prev)
        const existing = next.get(nodeId)
        const prevCount = existing?.suggestionCount ?? 0
        next.set(nodeId, {
          node: sourceNodeUpdate.node,
          operation: sourceNodeUpdate.operation ?? existing?.operation ?? 'generate_idea',
          isLoading: false,
          error: null,
          suggestionCount: prevCount + newSuggestions.length,
          lastUpdated: Date.now(),
          viewed: false,
          feedbackHistory: [
            ...(existing?.feedbackHistory ?? []),
            ...(sourceNodeUpdate.feedbackEntry ? [sourceNodeUpdate.feedbackEntry] : []),
          ],
        })
        return next
      })
    },
    []
  )

  const getFeedbackHistory = useCallback(
    (nodeId: string): string[] | undefined => {
      return sourceNodes.get(nodeId)?.feedbackHistory
    },
    [sourceNodes]
  )

  return {
    isProcessing,
    currentOperation,
    pendingOperations,
    suggestions,
    error,
    sourceNodes,
    suggestionsLoaded,
    generateIdeas,
    generateNeedsFromVenture,
    synthesizeNeedsFromResearch,
    toggleSuggestion,
    getSuggestionsForNode,
    dismissSourceNode: handleDismissSourceNode,
    markSuggestionNodeDeleted: handleMarkDeleted,
    addChatSuggestions,
    getFeedbackHistory,
    isOperationProcessing,
  }
}
